/**
 * Outline Tool — "create outline"
 *
 * User-driven outline creation. The user clicks rooms to add/remove them
 * from a working set; a live preview of the inflated/unioned outline (outer
 * ring + holes) is drawn for the current selection. Pressing Enter commits
 * the preview as one outline per connected component via `create_outline`;
 * each component's holes are persisted as cavities on the resulting outline.
 *
 * Wall thickness is exposed as a tool setting.
 */

import { FloorplanToolBase } from './FloorplanToolBase';
import { IViewConfig } from '../../types';
import { ToolSettingsSchema, ToolSettingsChangeEvent } from '../../ToolSettings';
import { ViewModes } from '../../helpers';
import { buildWallRegionFromRooms, WallRegionComponent } from '../../../utils/WallRegionBuilder';
import { PointoramaRoom } from '../../../utils/PointoramaRoom';
import { getPointCloudIntersection } from '../modules/shared';
import { metersToDisplay, displayToMeters } from '../modules/line-detection/distanceUnits';
import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { t } from '../../../translation/markers';

const DEFAULT_WALL_THICKNESS_M = 0.3;
const PREVIEW_COLOR = 0x59cd90;

/** Everything a `commitOutlines` run produced — consumed by WallCreateTool to
 * generate the matching wall sketch without recomputing or waiting for the
 * async `outline_added` round-trip (the identifiers are pre-minted). */
export interface OutlineCommitResult {
  /** One connected outer-wall component per disconnected wing (holes = cavities). */
  components: WallRegionComponent[];
  rooms: { identifier: string; polygon: THREE.Vector3[] }[];
  floorGroupIdentifier: string;
  floorHeight: number;
}

/** Wall-thickness slider bounds + granularity, in meters (converted to the
 *  user's display unit in `getSettingsSchema`). */
const WALL_THICKNESS_MIN_M = 0.05;
const WALL_THICKNESS_MAX_M = 1.0;
const WALL_THICKNESS_STEP_M = 0.01;

export class OutlineTool extends FloorplanToolBase {
  readonly name: string = 'outline_create';

  protected wallThickness = DEFAULT_WALL_THICKNESS_M;
  /**
   * Wall-region components computed by the last `refreshPreview` run, or null
   * when nothing is previewed. Lets subclasses (WallCreateTool) feed the same
   * components into their own derived previews / commits without recomputing
   * the room inflation + union.
   */
  protected lastComponents: WallRegionComponent[] | null = null;
  private previewGroup: THREE.Group | null = null;
  private readonly previewMaterials: LineMaterial[] = [];
  /** Room currently highlighted by our own hover; null if hover highlight is not owned by us. */
  private hoverOwnedRoomId: string | null = null;
  /**
   * Floor group identifier currently in Redux selection, or null. When a floor
   * group is in Redux, its child rooms are implicitly highlighted (via
   * `useFlatAnnotationList` expansion) but not individually present in the
   * selection array — toggling a child by id is a no-op because
   * `removeChildrenOfSelectedGroups` filters it back out. Track this so the
   * click handler can convert the group selection into explicit per-room
   * entries before deselecting one.
   */
  private reduxSelectedGroupId: string | null = null;

  getViewConfig(): IViewConfig {
    return {
      views: [ViewModes.XRAY_2D, ViewModes.ORTHOGRAPHIC_2D_TOP, ViewModes.PERSPECTIVE_3D_RGB],
      defaultViewId: ViewModes.XRAY_2D.id,
    };
  }

  getSettingsSchema(): ToolSettingsSchema {
    // Thickness is stored in meters but presented in the user's display unit
    // (m / ft). Convert the bounds + current value out; `onSettingsChange`
    // converts the user's entry back to meters.
    const unit = this.viewer.lengthUnitDisplay;
    return {
      wallThickness: {
        type: 'slider',
        label: t('Outer wall thickness'),
        description: 'Wall thickness used to inflate room polygons',
        defaultValue: metersToDisplay(this.wallThickness, unit),
        min: metersToDisplay(WALL_THICKNESS_MIN_M, unit),
        max: metersToDisplay(WALL_THICKNESS_MAX_M, unit),
        step: metersToDisplay(WALL_THICKNESS_STEP_M, unit),
        unit: unit.code,
        decimals: 2,
      },
    };
  }

  onSettingsChange(event: ToolSettingsChangeEvent): void {
    if (event.key === 'wallThickness') {
      // Slider value is in the display unit — store back in meters.
      this.wallThickness = displayToMeters(event.value as number, this.viewer.lengthUnitDisplay);
      this.refreshPreview();
    }
  }

  /** Rebuild the settings panel when the display unit changes so the thickness
   *  slider re-scales to the new unit live (the panel is built once and cached,
   *  so it won't otherwise pick up a mid-session unit swap). */
  private readonly handleUnitChanged = (): void => {
    this.getSettingsPanel()?.rebuild(this.getSettingsSchema());
  };

  protected onEnable(): void {
    super.onEnable();
    this.viewer.renderer.domElement.style.cursor = 'crosshair';
    this.viewer.addEventListener('length_unit_changed', this.handleUnitChanged);
    // Suppress the legacy `viewer.selectionTool` auto-click handler so it
    // doesn't clear our Redux selection on every canvas click.
    this.viewer.inputHandler.selectionDisabled = true;
    // Redux-driven selection (left-panel clicks) round-trips through this event.
    this.viewer.scene.addEventListener('objects_selection_changed', this.handleObjectsSelectionChanged);
    // Selection may already be non-empty when the tool enables.
    this.refreshPreview();
    this.dispatchPhase();
    // Ask the React side to re-emit the current selection so we pick up any
    // floor group that was already selected before the tool enabled.
    this.viewer.scene.dispatchEvent({ type: 'objects_request' });
  }

  protected onDisable(): void {
    this.viewer.inputHandler.selectionDisabled = false;
    this.viewer.removeEventListener('length_unit_changed', this.handleUnitChanged);
    this.viewer.scene.removeEventListener('objects_selection_changed', this.handleObjectsSelectionChanged);
    this.clearPreview();
    this.releaseHover();
    this.viewer.renderer.domElement.style.cursor = 'default';
    super.onDisable();
  }

  protected onFloorChanged(): void {
    this.releaseHover();
    this.refreshPreview();
  }

  private handleObjectsSelectionChanged = (event: { selectedGroupIdentifier?: string }): void => {
    this.reduxSelectedGroupId = event.selectedGroupIdentifier ?? null;
    this.refreshPreview();
    this.dispatchPhase();
  };

  /** Tell the quest system which tooltip to show, based on whether any rooms are selected. */
  protected dispatchPhase(): void {
    const phase = this.getSelectedRooms().length > 0 ? 'has_selection' : 'idle';
    this.dispatchStateChanged({ phase });
  }

  public onMouseMove(_event: MouseEvent): void {
    const room = this.pickRoom();
    const newHoverId = room?.identifier ?? null;

    this.viewer.renderer.domElement.style.cursor = room ? 'pointer' : 'crosshair';

    if (newHoverId !== this.hoverOwnedRoomId) {
      this.releaseHover();
      // Only own the highlight if Redux isn't already highlighting it.
      if (room && !room.selected) {
        room.select();
        this.hoverOwnedRoomId = room.identifier;
      }
    }
  }

  public onClick(event: MouseEvent): void {
    if (event.button !== 0) return;
    const room = this.pickRoom();
    if (!room) return;

    // We're about to hand the highlight to Redux — release any hover ownership
    // so the upcoming objects_selection_changed event doesn't get nullified by
    // a subsequent hover-deselect.
    if (this.hoverOwnedRoomId === room.identifier) {
      this.hoverOwnedRoomId = null;
    }

    const floorGroupId = this.currentFloorGroupIdentifier;
    if (floorGroupId && this.reduxSelectedGroupId === floorGroupId) {
      // Floor group is Redux-selected → its rooms are highlighted implicitly.
      // Toggling one by id would be filtered out by `removeChildrenOfSelectedGroups`,
      // so convert the implicit group selection into explicit per-room entries
      // (excluding the clicked room) before letting normal toggles take over.
      this.convertGroupSelectionToExplicitRooms(floorGroupId, room.identifier);
      return;
    }

    // Round-trip through Redux so the left panel stays in sync. The resulting
    // `objects_selection_changed` event refreshes our preview.
    this.viewer.scene.dispatchEvent({
      type: 'measurement_select',
      value: { measure: room, isCtrl: true },
    });
  }

  /**
   * Replace the floor-group Redux entry with explicit per-room entries for
   * every room on the floor except `excludedRoomId`. Achieves the deselect-one
   * effect when the user clicks a room that was highlighted only because its
   * parent group sits in Redux.
   */
  private convertGroupSelectionToExplicitRooms(floorGroupId: string, excludedRoomId: string): void {
    const roomsToKeep = (this.viewer.scene.rooms as PointoramaRoom[]).filter((r) => r.groupIdentifier === floorGroupId && r.identifier !== excludedRoomId);

    // Clear first (drops the group entry), then add each room individually.
    // Both events fire synchronously through Redux dispatch, landing as a
    // single React render.
    this.viewer.scene.dispatchEvent({
      type: 'measurement_select',
      value: { measure: null, isCtrl: false },
    });
    for (const r of roomsToKeep) {
      this.viewer.scene.dispatchEvent({
        type: 'measurement_select',
        value: { measure: r, isCtrl: true },
      });
    }
  }

  private findRoomById(id: string): PointoramaRoom | null {
    return (this.viewer.scene.rooms as PointoramaRoom[]).find((r) => r.identifier === id) ?? null;
  }

  /** Drop our hover highlight (no-op if the room is also Redux-selected). */
  private releaseHover(): void {
    if (!this.hoverOwnedRoomId) return;
    const room = this.findRoomById(this.hoverOwnedRoomId);
    if (room && !this.isReduxSelected(room)) room.deselect();
    this.hoverOwnedRoomId = null;
  }

  /** A room is Redux-selected if it's in scene.rooms with `selected === true` and isn't our hover. */
  private isReduxSelected(room: PointoramaRoom): boolean {
    if (!room.selected) return false;
    return room.identifier !== this.hoverOwnedRoomId;
  }

  /** Rooms on the active floor that the user has chosen for the outline. */
  protected getSelectedRooms(): PointoramaRoom[] {
    const floorGroupId = this.currentFloorGroupIdentifier;
    if (!floorGroupId) return [];
    return (this.viewer.scene.rooms as PointoramaRoom[]).filter((r) => r.groupIdentifier === floorGroupId && this.isReduxSelected(r));
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      // Clear Redux selection — the round-trip drops all room highlights and refreshes us.
      this.viewer.scene.dispatchEvent({
        type: 'measurement_select',
        value: { measure: null, isCtrl: false },
      });
    }
  }

  /** Pick the room currently under the cursor, restricted to the active floor. */
  private pickRoom(): PointoramaRoom | null {
    const floorGroupId = this.currentFloorGroupIdentifier;
    if (!floorGroupId) return null;

    const rooms = (this.viewer.scene.rooms as PointoramaRoom[]).filter((r) => r.visible && r.groupIdentifier === floorGroupId);
    if (rooms.length === 0) return null;

    const camera = this.viewer.scene.getActiveCamera();
    const is3D = camera.type === 'PerspectiveCamera';
    const raycaster = this.viewer.inputHandler.raycaster;
    const mouse = this.viewer.inputHandler.mouse;

    // Edge raycast against room background lines.
    const intersect = raycaster.intersectObjects(rooms.map((r) => r.backgroundLine)).at(0);
    if (intersect) {
      if (is3D) {
        const pointCloudHit = getPointCloudIntersection(this.viewer, mouse);
        if (pointCloudHit) {
          const distToPointCloud = camera.position.distanceTo(pointCloudHit);
          if (intersect.distance > distToPointCloud * 1.1) {
            // edge is behind the cloud — fall through to interior test
          } else {
            return this.resolveRoomFromObject(intersect.object);
          }
        } else {
          return this.resolveRoomFromObject(intersect.object);
        }
      } else {
        return this.resolveRoomFromObject(intersect.object);
      }
    }

    // Interior point-in-polygon fallback (works in both 2D and 3D ortho-top).
    const worldPoint = this.projectMouseToFloor();
    if (!worldPoint) return null;
    for (const room of rooms) {
      const polygon = room.getPolygon();
      if (polygon.length < 3) continue;
      if (this.isPointInPolygon(worldPoint, polygon)) return room;
    }
    return null;
  }

  private resolveRoomFromObject(obj: THREE.Object3D): PointoramaRoom | null {
    let parent: (THREE.Object3D & { identifier?: string }) | null = obj.parent as (THREE.Object3D & { identifier?: string }) | null;
    while (parent && !parent.identifier) {
      parent = parent.parent as (THREE.Object3D & { identifier?: string }) | null;
    }
    return (parent as PointoramaRoom) || null;
  }

  /** Project the current mouse onto the active floor plane (z = floorHeight). */
  private projectMouseToFloor(): THREE.Vector3 | null {
    const floorHeight = this.currentFloorFloorPlane?.height;
    if (floorHeight === undefined) return null;
    const ray = this.viewer.inputHandler.raycaster.ray;
    if (Math.abs(ray.direction.z) < 1e-6) return null;
    const t = (floorHeight - ray.origin.z) / ray.direction.z;
    if (t < 0) return null;
    return new THREE.Vector3().copy(ray.direction).multiplyScalar(t).add(ray.origin);
  }

  private isPointInPolygon(point: THREE.Vector3, vertices: THREE.Vector3[]): boolean {
    let inside = false;
    for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
      const xi = vertices[i].x,
        yi = vertices[i].y;
      const xj = vertices[j].x,
        yj = vertices[j].y;
      if (yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  }

  /** Recompute the wall-region preview for the current selection. */
  protected refreshPreview(): void {
    this.clearPreview();
    this.lastComponents = null;

    const floorPlane = this.currentFloorFloorPlane;
    if (!floorPlane) return;

    const rooms = this.getSelectedRooms();
    if (rooms.length === 0) return;

    const roomData = rooms.map((r) => ({ identifier: r.identifier, polygon: r.getPolygon() }));
    const components = buildWallRegionFromRooms(roomData, this.wallThickness);
    if (components.length === 0) return;

    this.lastComponents = components;
    this.renderPreview(components, floorPlane.height);
  }

  protected renderPreview(components: WallRegionComponent[], floorHeight: number): void {
    const group = new THREE.Group();
    group.name = 'outline-preview';

    // Center the group at the centroid of the first component's outer ring
    // (matches the floating-point-precision pattern used by PointoramaRoom).
    const first = components[0].outerRing;
    const cx = first.reduce((s, v) => s + v.x, 0) / first.length;
    const cy = first.reduce((s, v) => s + v.y, 0) / first.length;
    group.position.set(cx, cy, floorHeight);

    const resolution = new THREE.Vector2(this.viewer.renderer.domElement.clientWidth, this.viewer.renderer.domElement.clientHeight);
    const material = new LineMaterial({
      color: PREVIEW_COLOR,
      linewidth: 3,
      resolution,
      depthTest: false,
      transparent: true,
      opacity: 0.95,
    });
    this.previewMaterials.push(material);

    for (const comp of components) {
      this.addRingLine(group, comp.outerRing, cx, cy, material);
      for (const hole of comp.holes) {
        this.addRingLine(group, hole, cx, cy, material);
      }
    }

    this.previewGroup = group;
    this.viewer.measurementScene.add(group);
  }

  private addRingLine(group: THREE.Group, ring: THREE.Vector2[], cx: number, cy: number, material: LineMaterial): void {
    if (ring.length < 2) return;
    const positions: number[] = [];
    for (const v of ring) {
      positions.push(v.x - cx, v.y - cy, 0);
    }
    // Close the loop.
    positions.push(ring[0].x - cx, ring[0].y - cy, 0);
    const geometry = new LineGeometry();
    geometry.setPositions(positions);
    const line = new Line2(geometry, material);
    line.computeLineDistances();
    group.add(line);
  }

  protected clearPreview(): void {
    if (this.previewGroup) {
      this.viewer.measurementScene.remove(this.previewGroup);
      this.previewGroup.traverse((child) => {
        const line = child as Line2;
        if (line.geometry) line.geometry.dispose();
      });
      this.previewGroup = null;
    }
    for (const mat of this.previewMaterials) mat.dispose();
    this.previewMaterials.length = 0;
  }

  protected commit(): void {
    this.commitOutlines();
  }

  /**
   * Resolve the current room selection into outer-wall components (one per
   * connected component) and clear the Redux selection. Returns the
   * components so subclasses can persist them (merged outline rings on the
   * wall sketch) and chain wall-sketch generation, or null when there was
   * nothing to commit.
   */
  protected commitOutlines(): OutlineCommitResult | null {
    const floorGroupId = this.currentFloorGroupIdentifier;
    const floorPlane = this.currentFloorFloorPlane;
    if (!floorGroupId || !floorPlane) return null;

    const rooms = this.getSelectedRooms();
    if (rooms.length === 0) return null;

    const roomData = rooms.map((r) => ({ identifier: r.identifier, polygon: r.getPolygon() }));
    const components = buildWallRegionFromRooms(roomData, this.wallThickness);
    if (components.length === 0) return null;

    // Since the outline merge, no OUTLINE annotation is created here — the
    // caller writes the components into the floor's wall sketch as merged
    // `role:'OUTLINE'` ring edges (see WallCreateTool.commit).
    const floorHeight = floorPlane.height;

    // Clear Redux selection — the round-trip drops highlights and refreshes us.
    this.viewer.scene.dispatchEvent({
      type: 'measurement_select',
      value: { measure: null, isCtrl: false },
    });

    return { components, rooms: roomData, floorGroupIdentifier: floorGroupId, floorHeight };
  }
}
